import { useState, useCallback, useEffect, type FormEvent } from 'react';
import { X } from 'lucide-react';
import { apiClient } from '@/api/client';
import type { TunnelConfig } from '@/types';

// ═══════════════════════════════════════════
// Props
// ═══════════════════════════════════════════

interface TunnelModalProps {
  tunnelId?: string;
  initialData?: Partial<TunnelConfig>;
  onClose: () => void;
  onSaved: () => void;
}

// ═══════════════════════════════════════════
// Constants
// ═══════════════════════════════════════════

type AuthMethod = 'key' | 'password';

const AUTH_METHOD_OPTIONS: { value: AuthMethod; label: string }[] = [
  { value: 'key', label: 'Private Key' },
  { value: 'password', label: 'Password' },
];

const COLOR_PRESETS = [
  '#4a7fff', '#3dd68c', '#f0c556', '#ef5350',
  '#b07fff', '#e0955c', '#4dd0c8', '#e06c75',
];

// ═══════════════════════════════════════════
// Form state
// ═══════════════════════════════════════════

interface FormState {
  name: string;
  host: string;
  port: number;
  username: string;
  authMethod: AuthMethod;
  /** Pasted private-key content. Never pre-filled from the server —
   *  keys are write-only on the wire. Empty on save = keep existing.
   *  See server `routes/tunnels.ts` PUT for the semantics. */
  privateKey: string;
  passphrase: string;
  password: string;
  localPort: string; // string so we can leave it empty for "auto"
  colorTag: string | null;
}

function buildInitialState(initialData?: Partial<TunnelConfig>): FormState {
  // Legacy data (TunnelConfig) might still carry an old auth_method
  // value like 'agent' from before the migration. Coerce it to 'key'
  // so the dropdown lands on a valid option; the actual auth_method
  // in the DB row is unchanged until the user saves.
  const am = initialData?.authMethod === 'password' ? 'password' : 'key';
  return {
    name: initialData?.name ?? '',
    host: initialData?.host ?? '',
    port: initialData?.port ?? 22,
    username: initialData?.username ?? '',
    authMethod: am,
    privateKey: '',
    passphrase: '',
    password: '',
    localPort: initialData?.localPort ? String(initialData.localPort) : '',
    colorTag: initialData?.colorTag ?? null,
  };
}

// ═══════════════════════════════════════════
// Reusable form primitives (matching ConnectionModal)
// ═══════════════════════════════════════════

const inputClass =
  'w-full bg-dbgo-input border rounded-sm px-2.5 py-[7px] text-[13px] text-text-primary placeholder:text-text-muted outline-none focus:border-focus transition-colors';

const labelClass = 'block text-xs font-semibold text-text-secondary mb-1.5';

const selectClass =
  'w-full bg-dbgo-input border rounded-sm px-2.5 py-[7px] text-[13px] text-text-primary outline-none focus:border-focus transition-colors appearance-none cursor-pointer';

function FormField({ label, htmlFor, children }: { label: string; htmlFor?: string; children: React.ReactNode }) {
  return (
    <div>
      <label htmlFor={htmlFor} className={labelClass}>{label}</label>
      {children}
    </div>
  );
}

function FormRow({ children }: { children: React.ReactNode }) {
  return <div className="grid grid-cols-2 gap-3">{children}</div>;
}

// ═══════════════════════════════════════════
// Component
// ═══════════════════════════════════════════

export function TunnelModal({ tunnelId, initialData, onClose, onSaved }: TunnelModalProps) {
  const [form, setForm] = useState<FormState>(() => buildInitialState(initialData));
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<{ ok: boolean; error?: string; latencyMs: number } | null>(null);
  const [error, setError] = useState('');

  const isEditing = !!tunnelId;

  const updateField = useCallback(<K extends keyof FormState>(key: K, value: FormState[K]) => {
    setForm(prev => ({ ...prev, [key]: value }));
  }, []);

  // Close on Escape
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  const buildPayload = useCallback(() => {
    const payload: Record<string, unknown> = {
      name: form.name,
      host: form.host,
      port: form.port,
      username: form.username,
      authMethod: form.authMethod,
      colorTag: form.colorTag,
    };
    if (form.localPort.trim()) {
      payload.localPort = parseInt(form.localPort, 10) || undefined;
    }
    if (form.authMethod === 'key') {
      // Empty privateKey on edit = keep existing. Server treats
      // "" / undefined the same way; only a non-empty paste updates.
      payload.privateKey = form.privateKey || undefined;
      payload.passphrase = form.passphrase || undefined;
    }
    if (form.authMethod === 'password') {
      payload.password = form.password || undefined;
    }
    return payload;
  }, [form]);

  const handleTest = useCallback(async () => {
    if (!form.host.trim() || !form.username.trim()) {
      setError('SSH host and username are required to test');
      return;
    }
    setTesting(true);
    setTestResult(null);
    setError('');
    try {
      const payload = {
        // tunnelId lets the server fall back to the stored encrypted
        // key / passphrase / password when the form fields are blank —
        // the modal never pre-fills secrets, so an "edit, then Test"
        // flow has to round-trip through the stored values.
        tunnelId: tunnelId || undefined,
        host: form.host,
        port: form.port,
        username: form.username,
        authMethod: form.authMethod,
        privateKey: form.authMethod === 'key' ? form.privateKey : undefined,
        passphrase: form.authMethod === 'key' ? form.passphrase : undefined,
        password: form.authMethod === 'password' ? form.password : undefined,
      };
      const result = await apiClient.post<{ ok: boolean; error?: string; latencyMs: number }>('/api/tunnels/test', payload);
      setTestResult(result);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Test failed';
      setTestResult({ ok: false, error: message, latencyMs: 0 });
    } finally {
      setTesting(false);
    }
  }, [form, tunnelId]);

  const handleSave = useCallback(async (e: FormEvent) => {
    e.preventDefault();

    if (!form.name.trim()) {
      setError('Tunnel name is required');
      return;
    }
    if (!form.host.trim()) {
      setError('SSH host is required');
      return;
    }
    if (!form.username.trim()) {
      setError('SSH username is required');
      return;
    }

    setSaving(true);
    setError('');
    try {
      const payload = buildPayload();
      if (isEditing) {
        await apiClient.put(`/api/tunnels/${tunnelId}`, payload);
      } else {
        await apiClient.post('/api/tunnels', payload);
      }
      onSaved();
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to save tunnel';
      setError(message);
    } finally {
      setSaving(false);
    }
  }, [form, isEditing, tunnelId, buildPayload, onSaved]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-stretch justify-center bg-black/60 backdrop-blur-sm sm:items-center"
      style={{
        paddingTop: 'env(safe-area-inset-top)',
        paddingBottom: 'env(safe-area-inset-bottom)',
      }}
    >
      <form
        onSubmit={handleSave}
        className="w-full flex flex-col bg-dbgo-modal border shadow-2xl animate-fade-in sm:w-[580px] sm:max-h-[80vh] sm:rounded-lg"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 pt-4 pb-0">
          <h2 className="text-sm font-semibold text-text-primary">
            {isEditing ? 'Edit Tunnel' : 'New SSH Tunnel'}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="w-6 h-6 flex items-center justify-center rounded-sm text-text-muted hover:text-text-secondary hover:bg-dbgo-tab-hover transition-colors"
          >
            <X size={14} />
          </button>
        </div>

        {/* Form content */}
        <div className="flex-1 overflow-y-auto px-5 py-4 min-h-0">
          <div className="flex flex-col gap-3">
            <FormField label="Name" htmlFor="tunnel-name">
              <input
                id="tunnel-name"
                type="text"
                value={form.name}
                onChange={e => updateField('name', e.target.value)}
                placeholder="Production Bastion"
                autoFocus
                className={inputClass}
              />
            </FormField>

            <FormRow>
              <FormField label="SSH Host" htmlFor="tunnel-host">
                <input
                  id="tunnel-host"
                  type="text"
                  value={form.host}
                  onChange={e => updateField('host', e.target.value)}
                  placeholder="bastion.example.com"
                  className={inputClass}
                />
              </FormField>
              <FormField label="SSH Port" htmlFor="tunnel-port">
                <input
                  id="tunnel-port"
                  type="number"
                  value={form.port}
                  onChange={e => updateField('port', parseInt(e.target.value, 10) || 22)}
                  className={inputClass}
                />
              </FormField>
            </FormRow>

            <FormField label="SSH Username" htmlFor="tunnel-username">
              <input
                id="tunnel-username"
                type="text"
                value={form.username}
                onChange={e => updateField('username', e.target.value)}
                placeholder="root"
                className={inputClass}
              />
            </FormField>

            <FormField label="Auth Method" htmlFor="tunnel-auth">
              <select
                id="tunnel-auth"
                value={form.authMethod}
                onChange={e => updateField('authMethod', e.target.value as AuthMethod)}
                className={selectClass}
              >
                {AUTH_METHOD_OPTIONS.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </FormField>

            {form.authMethod === 'key' && (
              <>
                <FormField label="Private Key" htmlFor="tunnel-key">
                  <textarea
                    id="tunnel-key"
                    value={form.privateKey}
                    onChange={e => updateField('privateKey', e.target.value)}
                    placeholder={
                      (initialData as { hasPrivateKey?: boolean } | undefined)?.hasPrivateKey
                        ? 'A key is stored. Paste a new one to replace, or leave blank to keep it.'
                        : '-----BEGIN OPENSSH PRIVATE KEY-----\n…\n-----END OPENSSH PRIVATE KEY-----'
                    }
                    rows={6}
                    spellCheck={false}
                    autoCapitalize="off"
                    autoComplete="off"
                    autoCorrect="off"
                    className={`${inputClass} font-mono resize-y min-h-[120px]`}
                  />
                  <p className="mt-1 text-2xs text-text-muted">
                    Paste the contents of your key file (e.g. <code>~/.ssh/id_ed25519</code>). Stored encrypted with the server's JWT_SECRET; never read from disk.
                  </p>
                </FormField>
                <FormField label="Passphrase" htmlFor="tunnel-passphrase">
                  <input
                    id="tunnel-passphrase"
                    type="password"
                    value={form.passphrase}
                    onChange={e => updateField('passphrase', e.target.value)}
                    placeholder="Optional"
                    className={inputClass}
                  />
                </FormField>
              </>
            )}

            {form.authMethod === 'password' && (
              <FormField label="SSH Password" htmlFor="tunnel-password">
                <input
                  id="tunnel-password"
                  type="password"
                  value={form.password}
                  onChange={e => updateField('password', e.target.value)}
                  className={inputClass}
                />
              </FormField>
            )}

            {/* Separator */}
            <div className="border-t my-1" />

            <FormField label="Local Port" htmlFor="tunnel-local-port">
              <input
                id="tunnel-local-port"
                type="text"
                value={form.localPort}
                onChange={e => updateField('localPort', e.target.value)}
                placeholder="Auto (leave empty)"
                className={inputClass}
              />
            </FormField>

            {/* Color tag */}
            <FormField label="Color Tag">
              <div className="flex items-center gap-2 mt-1">
                <button
                  type="button"
                  onClick={() => updateField('colorTag', null)}
                  className={`w-6 h-6 rounded-full border-2 transition-colors flex items-center justify-center ${
                    form.colorTag === null ? 'border-text-primary' : 'border-strong hover:border-text-muted'
                  }`}
                  title="No color"
                >
                  {form.colorTag === null && (
                    <span className="w-2 h-2 rounded-full bg-text-primary" />
                  )}
                </button>
                {COLOR_PRESETS.map(color => (
                  <button
                    key={color}
                    type="button"
                    onClick={() => updateField('colorTag', color)}
                    className={`w-6 h-6 rounded-full border-2 transition-colors ${
                      form.colorTag === color ? 'border-text-primary scale-110' : 'border-transparent hover:border-strong'
                    }`}
                    style={{ backgroundColor: color }}
                    title={color}
                  />
                ))}
              </div>
            </FormField>
          </div>
        </div>

        {/* Error */}
        {error && (
          <div className="px-5 pb-2 text-xs text-accent-red">{error}</div>
        )}

        {/* Test result */}
        {testResult && (
          <div className={`px-5 pb-2 text-xs ${testResult.ok ? 'text-accent-green' : 'text-accent-red'}`}>
            {testResult.ok
              ? `SSH connection successful (${testResult.latencyMs}ms)`
              : `SSH connection failed: ${testResult.error}`}
          </div>
        )}

        {/* Footer */}
        <div className="flex items-center justify-end px-5 py-3 border-t gap-2">
          <button
            type="button"
            onClick={handleTest}
            disabled={testing || saving}
            className="mr-auto px-3 py-[6px] text-xs font-medium text-accent-green border border-accent-green/40 hover:border-accent-green/70 disabled:opacity-50 disabled:cursor-not-allowed rounded-sm transition-colors"
          >
            {testing ? 'Testing...' : 'Test SSH'}
          </button>
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="px-3 py-[6px] text-xs font-medium text-text-secondary hover:text-text-primary rounded-sm hover:bg-dbgo-tab-hover transition-colors"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={saving}
            className="px-4 py-[6px] text-xs font-medium text-white bg-accent-blue hover:bg-accent-blue/90 disabled:opacity-50 disabled:cursor-not-allowed rounded-sm transition-colors"
          >
            {saving ? 'Saving...' : 'Save'}
          </button>
        </div>
      </form>
    </div>
  );
}
